import {
    CLEAR_NOTIFICATION_DETAILS,
    iWebsocket,
    iWebsocketDisconnect,
    NotificationAction,
    PDF_CREATING,
    PDF_ERROR,
    PDF_READY,
    SET_NOTIFICATION_ACTION,
    SET_NOTIFICATION_MESSAGE,
    tNotificationTypes,
    tWebsocketActions,
    WS_CONNECTED,
    WS_DISCONNECTED,
    WS_MESSAGE_RECEIVED,
} from "./types"

import { iActionData } from "../common/types"
import { downloadFile } from "./actions"

/** An employee whose Autodesk hub membership or license seat could not be set up or removed. */
export type tAutodeskAccessFailure = {
    employee_id: number
    name: string
    company_supplied_id: string
    reason: string
}

/**
 * The toast text for a batch of Autodesk access failures.
 * @param count - how many employees are affected
 */
export const autodeskAccessFailuresMessage = (count: number): string => `Autodesk access could not be updated for ${count} ${count === 1 ? "employee" : "employees"}. Click for details.`

export type tSocketNotificationState = {
    connected: boolean
    filename?: string
    // if the user takes the action, they don't always need a notification
    ignoreIds?: Record<string, number[] | undefined>
    message?: string
    notificationType?: tNotificationTypes
    pdfMessage?: typeof PDF_READY | typeof PDF_CREATING | typeof PDF_ERROR // used on single edit pdf download
    // Keyed by host so that concurrently open connections (e.g. field forms and a TK export) don't
    // overwrite each other's socket reference.
    sockets: Record<string, iWebsocket | null>
    showNotification: boolean
    uniqueRequestId?: string
    updatedIds?: Record<string, number[] | undefined>
    autoCloseSeconds?: number // if given will auto-close after provided number of seconds
    className?: string
    notificationAction?: NotificationAction // action button for the notification (e.g., "Reload")
    entriesTransitionedCount?: number // number of entries transitioned during export
    autodeskAccessFailures?: tAutodeskAccessFailure[] // employees whose Autodesk access could not be updated, until dismissed
}

const websocketNotificationInitialState: tSocketNotificationState = {
    connected: false,
    filename: undefined,
    ignoreIds: {
        companyFormStores: undefined,
    },
    message: undefined,
    pdfMessage: undefined,
    notificationType: "info",
    showNotification: false,
    sockets: {},
    uniqueRequestId: undefined,
    updatedIds: {
        companyFormStores: undefined,
    },
    autoCloseSeconds: undefined,
    notificationAction: undefined,
    entriesTransitionedCount: undefined,
    autodeskAccessFailures: undefined,
}

/**
 * Sockets are tracked per host so that concurrently open connections (e.g. Field Forms Bulk Modify
 * and a TK export) each keep their own reference instead of overwriting one another.
 * updatedIds and ignoreIds currently have nested id lists based on resource
 * (currently field forms is the only place using them). It is unclear what future use cases might be,
 * so that nesting may prove to be unnecessary. Open to the idea of removing that nesting if it's not needed.
 * Sockets are currently being used for Field Forms Bulk Modify and PDF Download.
 */
export const socketNotification = (state = { ...websocketNotificationInitialState }, action: tWebsocketActions): tSocketNotificationState => {
    switch (action.type) {
        case WS_CONNECTED:
            if ("socket" in action) {
                return { ...state, connected: true, sockets: { ...state.sockets, [action.host]: action.socket } }
            } else {
                return { ...state, ...websocketNotificationInitialState }
            }
        case WS_MESSAGE_RECEIVED:
            if ("event" in action) {
                const e = action.event
                // if the data is a Blob it is a file from the server
                // check for uniqueRequestId before saving so we don't download in a browser that didn't request it
                if (e.data instanceof Blob) {
                    // if the filename and request id are not set, the request wasn't made by this browser
                    if (!state?.filename || !state?.uniqueRequestId) {
                        return { ...state }
                    }
                    // file download should trigger as soon as they arrive
                    downloadFile(e.data, state.filename)

                    // If entries were transitioned, show a notification prompting reload
                    if (state.entriesTransitionedCount && state.entriesTransitionedCount > 0) {
                        const transitionMsg = `Export complete. ${state.entriesTransitionedCount} entries ` + "transitioned. Reload to see updated statuses."
                        return {
                            ...state,
                            filename: undefined,
                            uniqueRequestId: undefined,
                            showNotification: true,
                            message: transitionMsg,
                            notificationType: "success",
                            // notificationAction is set by the component that consumes this state
                        }
                    }

                    // only close the notification if the current message is about a pending download
                    const closeNotification = state.message && state.message.toLowerCase().includes("download")
                    // once the file is download clear out the state's file details
                    return {
                        ...state,
                        filename: undefined,
                        uniqueRequestId: undefined,
                        // close the notification if it was open, don't reopen if it it is already false
                        showNotification: state.showNotification && !closeNotification,
                        entriesTransitionedCount: undefined,
                        // if we're getting a file it's for download - no text message included with binary messages
                    }
                } else {
                    const data = JSON.parse(e.data)
                    const { ids: updatedIds, message, notification_type = "info" } = data
                    switch (message) {
                        case "Connected":
                            // don't display the initial connected message, just update connected status
                            return { ...state, connected: true }
                        case "File download ready":
                            // this message will come in right before a file message, so set filename and request id
                            const { filename, uniqueRequestId, entries_transitioned_count } = data
                            if (uniqueRequestId !== state.uniqueRequestId) {
                                // if the request id doesn't match, don't download
                                // could happen if user has multiple browser windows open
                                return { ...state }
                            }
                            return {
                                ...state,
                                filename,
                                uniqueRequestId,
                                entriesTransitionedCount: entries_transitioned_count,
                            }
                        case "Autodesk Access Failures": {
                            const incoming: tAutodeskAccessFailure[] = data.failures ?? []
                            if (!incoming.length) return state
                            // Keep failures nobody has looked at yet, so two batches finishing close together
                            // don't lose the first one's.
                            const seen = new Set((state.autodeskAccessFailures ?? []).map(f => `${f.employee_id}:${f.reason}`))
                            const autodeskAccessFailures = [...(state.autodeskAccessFailures ?? []), ...incoming.filter(f => !seen.has(`${f.employee_id}:${f.reason}`))]
                            return {
                                ...state,
                                message: autodeskAccessFailuresMessage(autodeskAccessFailures.length),
                                notificationType: "warning",
                                showNotification: true,
                                autodeskAccessFailures,
                            }
                        }
                        case "TK Export Update":
                            // This is listened to elsewhere in the export_csv reducer. We ignore
                            // so that we don't wind up in the default case below.
                            return state
                        case PDF_READY:
                            // this will come after a new PDF is generated - remove the id from ignoreIds
                            const updatedIgnore = Array.isArray(state.ignoreIds?.companyFormStores) ? state.ignoreIds?.companyFormStores.filter(id => id !== updatedIds[0]) : []

                            return {
                                ...state,
                                pdfMessage: message,
                                ignoreIds: {
                                    companyFormStores: updatedIgnore,
                                },
                            }
                        case PDF_ERROR:
                            // this will come if a PDF fails to generate
                            return { ...state, pdfMessage: message }
                        default:
                            let idsToUpdate = updatedIds
                            if (state?.ignoreIds?.companyFormStores) {
                                idsToUpdate = updatedIds.filter((id: number) => !state?.ignoreIds?.companyFormStores?.includes(id))
                            }
                            // we may get more specific about messages, but this works for the current use cases
                            return {
                                ...state,
                                message: message,
                                notificationType: notification_type,
                                showNotification: updatedIds?.length ? !!idsToUpdate?.length : true,
                                updatedIds: {
                                    companyFormStores: idsToUpdate,
                                },
                                // this allows us to unlock the download button in event of an error
                                uniqueRequestId: notification_type !== "error" ? state.uniqueRequestId : undefined,
                            }
                    }
                }
            } else {
                return { ...state }
            }
        case WS_DISCONNECTED: {
            const { host } = action as iWebsocketDisconnect
            if (!host) {
                // no host means every connection was torn down (e.g. logout) - safe to fully reset
                return { ...websocketNotificationInitialState }
            }
            const sockets = { ...state.sockets }
            delete sockets[host]
            if (!Object.keys(sockets).length) {
                // that was the last open connection, so reset as before
                return { ...websocketNotificationInitialState }
            }
            // other hosts are still connected - only forget this host's socket
            return { ...state, sockets }
        }
        case CLEAR_NOTIFICATION_DETAILS:
            // reset the notification so it is fresh for the next one
            return {
                ...websocketNotificationInitialState,
                sockets: state.sockets,
                connected: state.connected,
                uniqueRequestId: state?.uniqueRequestId,
                autoCloseSeconds: undefined,
                showNotification: false,
                notificationAction: undefined,
                entriesTransitionedCount: undefined,
            }
        case SET_NOTIFICATION_ACTION:
            // Set an action button for the notification
            const actionData = (action as iActionData).data
            return {
                ...state,
                notificationAction: actionData?.notificationAction ?? undefined,
            }
        case SET_NOTIFICATION_MESSAGE:
            // allows us to set notifications without a websocket message - can be used for progress messaging
            const { data } = action as iActionData
            const { message, notificationType = "info", uniqueRequestId, ignoreIds, autoCloseSeconds, className } = data
            if (message === PDF_CREATING) {
                // this will come in when new PDF is being generated
                // we update ignoreIds so the user doesn't get notified about their own changes
                return { ...state, pdfMessage: message, ignoreIds }
            }
            return {
                ...state,
                message,
                notificationType,
                showNotification: true,
                uniqueRequestId,
                autoCloseSeconds,
                className,
            }
        default:
            return state
    }
}
